import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { teacherAPI, commonAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';

const CaptureAssessment = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [assessmentData, setAssessmentData] = useState(null);
  const [assessmentFields, setAssessmentFields] = useState([]);
  const [academicSessions, setAcademicSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedClass, setSelectedClass] = useState('');

  // Initial values from URL params
  const initialSubjectId = searchParams.get('subject');
  const initialClassId = searchParams.get('class');
  
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [uploadMode, setUploadMode] = useState('merge');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const assessmentSchema = Yup.object().shape({
    academic_session_id: Yup.number().required('Academic session is required'),
    term_id: Yup.number().required('Term is required'),
    subject_id: Yup.number().required('Subject is required'),
    class_id: Yup.number().required('Class is required'),
    assessments: Yup.array().of(
      Yup.object().shape({
        student_reg: Yup.string().required(),
        student_name: Yup.string().required(),
        scores: Yup.array().of(
          Yup.object().shape({
            field_code: Yup.string().required(),
            score: Yup.number()
              .min(0, 'Score cannot be negative')
              .nullable(),
          })
        ),
      })
    ),
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedSession && selectedTerm && selectedSubject && selectedClass) {
      fetchAssessmentData();
    }
  }, [selectedSession, selectedTerm, selectedSubject, selectedClass]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [sessionsRes, subjectsRes] = await Promise.all([
        teacherAPI.getAcademicSessions(),
        teacherAPI.getAssignedSubjects(),
      ]);

      setAcademicSessions(sessionsRes.data);
      setAssignedSubjects(subjectsRes.data);

      // Set initial selections from URL params
      if (initialSubjectId && initialClassId) {
        const subject = subjectsRes.data.find(s => s.subject_id == initialSubjectId);
        if (subject) {
          setSelectedSubject(subject.subject_id);
          setSelectedClass(subject.class_id);
        }
      }

    } catch (error) {
      toast.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessmentData = async () => {
    try {
      setLoading(true);
      
      const params = {
        academic_session_id: selectedSession,
        term_id: selectedTerm,
        subject_id: selectedSubject,
        class_id: selectedClass,
      };

      const response = await teacherAPI.getAssessmentData(params);
      setAssessmentData(response.data);
      setAssessmentFields(response.data.assessment_fields || []);
      
      toast.success('Assessment data loaded successfully');
    } catch (error) {
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      console.log('Submitting values:', values);
      
      const formattedData = {
        academic_session_id: values.academic_session_id,
        term_id: values.term_id,
        subject_id: values.subject_id,
        class_id: values.class_id,
        assessments: values.assessments.map(assessment => ({
          student_reg: assessment.student_reg,
          student_name: assessment.student_name,
          scores: assessment.scores.map(score => ({
            field_code: score.field_code,
            score: parseFloat(score.score) || 0
          }))
        }))
      };
      
      console.log('Formatted data for API:', formattedData);
      
      await teacherAPI.captureAssessment(formattedData);
      toast.success('Assessment scores saved successfully');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateTotalScore = (scores) => {
    return scores.reduce((total, score, index) => {
      const field = assessmentFields[index];
      const fieldWeight = field?.weight || 1;
      return total + (score?.score || 0) * fieldWeight;
    }, 0);
  };

  const handleDownloadTemplate = async (includeStudents = true) => {
    try {
      if (!selectedSession || !selectedTerm || !selectedSubject || !selectedClass) {
        toast.error('Please select all parameters first');
        return;
      }
      
      setLoading(true);
      
      const fieldsQuery = selectedFields.length > 0 
        ? selectedFields.join(',')
        : 'all';
      
      const response = await teacherAPI.downloadAssessmentTemplate({
        academic_session_id: selectedSession,
        term_id: selectedTerm,
        subject_id: selectedSubject,
        class_id: selectedClass,
        include_students: includeStudents,
        include_fields: fieldsQuery
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const subjectName = assessmentData?.subject?.name || selectedSubject;
      const className = assessmentData?.class?.name || selectedClass;
      link.setAttribute('download', `assessment_template_${subjectName}_${className}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      if (!selectedSession || !selectedTerm || !selectedSubject || !selectedClass) {
        toast.error('Please select all parameters first');
        return;
      }
      
      setLoading(true);
      
      const fieldsQuery = selectedFields.length > 0 
        ? selectedFields.join(',')
        : 'all';
      
      const response = await teacherAPI.exportAssessmentData({
        academic_session_id: selectedSession,
        term_id: selectedTerm,
        subject_id: selectedSubject,
        class_id: selectedClass,
        include_fields: fieldsQuery
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const subjectName = assessmentData?.subject?.name || selectedSubject;
      const className = assessmentData?.class?.name || selectedClass;
      link.setAttribute('download', `assessment_data_${subjectName}_${className}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setImportFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      
      if (jsonData.length > 0) {
        const headers = jsonData[0];
        toast.success(`File loaded: ${headers.length} columns, ${jsonData.length - 1} rows`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    try {
      setImporting(true);
      
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await teacherAPI.uploadAssessmentScores(
        selectedSession,
        selectedTerm,
        selectedSubject,
        selectedClass,
        formData,
        { params: { update_mode: uploadMode } }
      );
      
      const { summary } = response.data;
      
      if (summary.errors.length > 0) {
        toast.error(
          `Processed with ${summary.errors.length} errors. ` +
          `${summary.successful} successful, ${summary.failed} failed`
        );
        console.log('Import errors:', summary.errors);
        fetchAssessmentData();
      } else {
        toast.success(`Successfully imported ${summary.successful} student records`);
        fetchAssessmentData();
      }
      
      setShowImportModal(false);
      setImportFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  const fetchTerms = async (sessionId) => {
    try {
      const response = await teacherAPI.getTerms(sessionId);
      setTerms(response.data);
    } catch (error) {
      toast.error('Failed to load terms');
    }
  };

  if (loading && !assessmentData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Capture Assessment Scores</h1>
          <p className="text-gray-600">Enter student assessment scores for the selected subject and class</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50 transition-colors flex items-center justify-center"
          >
            <i className="fas fa-file-import mr-2"></i>
            <span className="hidden sm:inline">Import Excel</span>
            <span className="sm:hidden">Import</span>
          </button>
          
          <div className="relative group">
            <button
              type="button"
              //  onClick={() => handleDownloadTemplate(true)}
               onClick={handleExportData}
              className="px-4 py-2 border border-green-300 rounded-lg text-green-700 hover:bg-green-50 transition-colors flex items-center justify-center"
            >
              <i className="fas fa-file-export mr-2"></i>
              <span className="hidden sm:inline">Export</span>
              <i className="fas fa-chevron-down ml-2 text-xs"></i>
            </button>
            <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <button
                onClick={() => handleDownloadTemplate(true)}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Download Template (with students)
              </button>
              <button
                onClick={() => handleDownloadTemplate(false)}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Download Template (blank)
              </button>
              <div className="border-t border-gray-200"></div>
              <button
                onClick={handleExportData}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Export Current Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Selection Form */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Assessment Parameters</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Academic Session *
            </label>
            <select
              value={selectedSession}
              onChange={(e) => {
                setSelectedSession(e.target.value);
                setSelectedTerm('');
                setTerms([]);
                if (e.target.value) {
                  fetchTerms(e.target.value);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Select Session</option>
              {academicSessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.academic_session}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Term *
            </label>
            <select
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!selectedSession || loading}
            >
              <option value="">Select Term</option>
              {terms.map((term) => (
                <option key={term.id} value={term.id}>
                  {term.term}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject *
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Select Subject</option>
              {assignedSubjects.map((subject) => (
                <option key={`${subject.subject_id}-${subject.class_id}`} value={subject.subject_id}>
                  {subject.subject_name} ({subject.class_name})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Class *
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!selectedSubject || loading}
            >
              <option value="">Select Class</option>
              {assignedSubjects
                .filter(s => s.subject_id == selectedSubject)
                .map((subject) => (
                  <option key={subject.class_id} value={subject.class_id}>
                    {subject.class_name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {selectedSession && selectedTerm && selectedSubject && selectedClass && assessmentData && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <i className="fas fa-info-circle text-green-500 mr-2"></i>
              <p className="text-green-700">
                Ready to capture scores for{' '}
                <strong>{assessmentData?.subject?.name}</strong> in{' '}
                <strong>{assessmentData?.class?.name}</strong> for{' '}
                <strong>{assessmentData?.term?.name}</strong> term
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Assessment Form */}
      {assessmentData && assessmentData.students && assessmentData.students.length > 0 && (
        <Formik
          initialValues={{
            academic_session_id: parseInt(selectedSession),
            term_id: parseInt(selectedTerm),
            subject_id: parseInt(selectedSubject),
            class_id: parseInt(selectedClass),
            assessments: assessmentData.students.map(student => ({
              student_reg: student.student_reg,
              student_name: student.student_name,
              scores: assessmentFields.map(field => {
                const existingScore = student.assessment_scores?.find(s => s.field_code === field.field_code);
                return {
                  field_code: field.field_code,
                  score: existingScore?.score || 0,
                  _max_score: field.max_score
                };
              }),
            })),
          }}
          validate={(values) => {
            const errors = {};
            
            if (values.assessments) {
              values.assessments.forEach((assessment, studentIndex) => {
                if (assessment.scores) {
                  assessment.scores.forEach((score, fieldIndex) => {
                    const field = assessmentFields[fieldIndex];
                    if (!field) return;
                    
                    const fieldName = `assessments[${studentIndex}].scores[${fieldIndex}].score`;
                    
                    if (score.score === undefined || score.score === null || score.score === '') {
                      if (field.is_compulsory) {
                        if (!errors.assessments) errors.assessments = [];
                        if (!errors.assessments[studentIndex]) errors.assessments[studentIndex] = {};
                        if (!errors.assessments[studentIndex].scores) errors.assessments[studentIndex].scores = [];
                        errors.assessments[studentIndex].scores[fieldIndex] = {
                          score: 'Required field'
                        };
                      }
                      return;
                    }
                    
                    const numValue = parseFloat(score.score);
                    
                    if (isNaN(numValue)) {
                      if (!errors.assessments) errors.assessments = [];
                      if (!errors.assessments[studentIndex]) errors.assessments[studentIndex] = {};
                      if (!errors.assessments[studentIndex].scores) errors.assessments[studentIndex].scores = [];
                      errors.assessments[studentIndex].scores[fieldIndex] = {
                        score: 'Must be a number'
                      };
                      return;
                    }
                    
                    if (numValue < 0) {
                      if (!errors.assessments) errors.assessments = [];
                      if (!errors.assessments[studentIndex]) errors.assessments[studentIndex] = {};
                      if (!errors.assessments[studentIndex].scores) errors.assessments[studentIndex].scores = [];
                      errors.assessments[studentIndex].scores[fieldIndex] = {
                        score: 'Score cannot be negative'
                      };
                      return;
                    }
                    
                    if (numValue > field.max_score) {
                      if (!errors.assessments) errors.assessments = [];
                      if (!errors.assessments[studentIndex]) errors.assessments[studentIndex] = {};
                      if (!errors.assessments[studentIndex].scores) errors.assessments[studentIndex].scores = [];
                      errors.assessments[studentIndex].scores[fieldIndex] = {
                        score: `Maximum score is ${field.max_score}`
                      };
                      return;
                    }
                  });
                }
              });
            }
            
            return errors;
          }}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, isSubmitting, setFieldValue, errors, touched, handleSubmit, handleBlur }) => (
            <Form onSubmit={handleSubmit} className="space-y-6">
              {/* Assessment Fields Header */}
              <div className="bg-white rounded-lg shadow p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
                  <h3 className="text-lg font-semibold text-gray-800">Assessment Fields</h3>
                  <div className="text-sm text-gray-500">
                    {assessmentFields.length} fields • {assessmentData.total_students} students
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Student
                        </th>
                        {assessmentFields.map((field) => (
                          <th key={field.field_code} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div>
                              <div>{field.display_name}</div>
                              <div className="text-xs text-gray-400">
                                Max: {field.max_score} • Weight: {field.weight}x
                                {field.is_compulsory && <span className="text-red-500 ml-1">*</span>}
                              </div>
                            </div>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Score
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {values.assessments.map((assessment, studentIndex) => (
                        <tr key={assessment.student_reg} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {assessment.student_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {assessment.student_reg}
                              </div>
                            </div>
                          </td>
                          
                          {assessment.scores.map((score, fieldIndex) => {
                            const field = assessmentFields[fieldIndex];
                            const fieldName = `assessments[${studentIndex}].scores[${fieldIndex}].score`;
                            const error = errors?.assessments?.[studentIndex]?.scores?.[fieldIndex]?.score;
                            const isTouched = touched?.assessments?.[studentIndex]?.scores?.[fieldIndex]?.score;
                            
                            return (
                              <td key={`${studentIndex}-${fieldIndex}`} className="px-4 py-3">
                                <div>
                                  <input
                                    type="number"
                                    name={fieldName}
                                    value={score.score}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === '') {
                                        setFieldValue(fieldName, '');
                                        return;
                                      }
                                      
                                      const numValue = parseFloat(value);
                                      if (!isNaN(numValue)) {
                                        if (numValue > field.max_score) {
                                          setFieldValue(fieldName, field.max_score);
                                        } else if (numValue < 0) {
                                          setFieldValue(fieldName, 0);
                                        } else {
                                          setFieldValue(fieldName, numValue);
                                        }
                                      }
                                    }}
                                    onBlur={(e) => {
                                      handleBlur(e);
                                      const value = e.target.value;
                                      if (value === '' && field.is_compulsory) {
                                        setFieldValue(fieldName, 0);
                                      }
                                    }}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                      error && isTouched 
                                        ? 'border-red-300' 
                                        : 'border-gray-300'
                                    }`}
                                    min="0"
                                    max={field.max_score}
                                    step="0.1"
                                    placeholder={field.is_compulsory ? "Required" : "Optional"}
                                  />
                                  {error && isTouched && (
                                    <div className="text-xs text-red-500 mt-1">{error}</div>
                                  )}
                                  <div className="text-xs text-gray-500 mt-1">
                                    0-{field.max_score}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                          
                          <td className="px-4 py-3">
                            <div className="text-lg font-bold text-green-600">
                              {calculateTotalScore(assessment.scores).toFixed(1)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Form-level error display */}
                {errors && Object.keys(errors).length > 0 && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>
                      <div>
                        <p className="text-red-700 font-medium mb-1">
                          Please fix the validation errors before submitting
                        </p>
                        <ul className="text-red-600 text-sm space-y-1">
                          {errors.assessments?.map((studentErrors, studentIndex) => 
                            studentErrors?.scores?.map((scoreError, fieldIndex) => 
                              scoreError?.score && (
                                <li key={`${studentIndex}-${fieldIndex}`}>
                                  • Student {studentIndex + 1}, {assessmentFields[fieldIndex]?.display_name}: {scoreError.score}
                                </li>
                              )
                            )
                          ).flat().filter(Boolean)}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    <div>
                      <h4 className="font-medium text-gray-900">Assessment Summary</h4>
                      <p className="text-sm text-gray-500">
                        Click "Save All Scores" to submit all scores at once
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {assessmentData.total_students} Students
                      </div>
                      <div className="text-sm text-gray-500">
                        {assessmentFields.length} Assessment Fields
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to clear all scores?')) {
                        values.assessments.forEach((_, studentIndex) => {
                          assessmentFields.forEach((_, fieldIndex) => {
                            setFieldValue(
                              `assessments[${studentIndex}].scores[${fieldIndex}].score`,
                              0
                            );
                          });
                        });
                      }
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <i className="fas fa-eraser mr-2"></i>
                    Clear All
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || (errors && Object.keys(errors).length > 0)}
                    className={`px-6 py-2 rounded-lg transition-colors ${
                      isSubmitting || (errors && Object.keys(errors).length > 0)
                        ? 'bg-gray-400 cursor-not-allowed border-gray-300'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Save All Scores
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}

      {/* Empty State */}
      {assessmentData && (!assessmentData.students || assessmentData.students.length === 0) && (
        <div className="bg-white rounded-lg shadow p-8 md:p-12 text-center">
          <i className="fas fa-users text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Found</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            There are no students enrolled in this class for the selected academic session and term.
          </p>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-6">
        <h4 className="font-medium text-blue-900 mb-2 flex items-center">
          <i className="fas fa-info-circle mr-2"></i>
          Assessment Capture Instructions
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-blue-700 text-sm">
          <div className="flex items-start">
            <span className="mr-2">•</span>
            <span>Enter scores for each student in the corresponding assessment fields</span>
          </div>
          <div className="flex items-start">
            <span className="mr-2">•</span>
            <span>Scores are automatically validated against maximum allowed values</span>
          </div>
          <div className="flex items-start">
            <span className="mr-2">•</span>
            <span>Compulsory fields are marked with a red asterisk (*)</span>
          </div>
          <div className="flex items-start">
            <span className="mr-2">•</span>
            <span>Total score is calculated automatically based on field weights</span>
          </div>
          <div className="flex items-start">
            <span className="mr-2">•</span>
            <span>You can save all scores at once using the "Save All Scores" button</span>
          </div>
          <div className="flex items-start">
            <span className="mr-2">•</span>
            <span>Use "Clear All" to reset all scores to zero</span>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Import Assessment Scores from Excel
                </h3>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                    <i className="fas fa-info-circle mr-2"></i>
                    Instructions
                  </h4>
                  <ul className="text-blue-700 text-sm space-y-1">
                    <li>• Download a template first to see the correct format</li>
                    <li>• Keep the "student_reg" column unchanged</li>
                    <li>• Maximum scores are shown in column headers</li>
                    <li>• Save your file as .xlsx before uploading</li>
                  </ul>
                </div>
                
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Excel File
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx,.xls"
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {importFile && (
                    <div className="mt-2 text-sm text-green-600 flex items-center">
                      <i className="fas fa-check-circle mr-2"></i>
                      Selected: {importFile.name}
                    </div>
                  )}
                </div>
                
                {/* Update Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Update Mode
                  </label>
                  <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="merge"
                        checked={uploadMode === 'merge'}
                        onChange={(e) => setUploadMode(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Merge - Update only provided fields</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="replace"
                        checked={uploadMode === 'replace'}
                        onChange={(e) => setUploadMode(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Replace - Overwrite all scores</span>
                    </label>
                  </div>
                </div>
                
                {/* Fields Selection (Optional) */}
                {assessmentFields.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Fields to Import (Optional)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {assessmentFields.map((field) => (
                        <label key={field.field_code} className="inline-flex items-center bg-gray-50 px-3 py-1 rounded border">
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(field.field_code)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFields([...selectedFields, field.field_code]);
                              } else {
                                setSelectedFields(selectedFields.filter(f => f !== field.field_code));
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">
                            {field.display_name} ({field.max_score})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Modal Actions */}
              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDownloadTemplate(true)}
                  className="px-4 py-2 border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50 transition-colors"
                  disabled={importing}
                >
                  <i className="fas fa-download mr-2"></i>
                  Download Template
                </button>
                <button
                  onClick={handleImportSubmit}
                  disabled={!importFile || importing}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    !importFile || importing
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {importing ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload mr-2"></i>
                      Upload and Process
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaptureAssessment;